import {Link} from 'react-router';

import {usePagination} from '@/hooks';

type profileDataVisualT = {
  title: string;
  data: {src: string; name: string; text: string}[];
  className?: string;
  badgeText?: string;
  itemsPerPage?: number;
};

/**
 * This component is used to render data with an abvatar
 * @param {string} title - The title of the data.
 * @param {object[]} data - The data to be rendered.
 * @param {string} [className] - The class name of the component.
 * @param {string} [badgeText] - The badge text to be displayed.
 * @param {number} [itemsPerPage] - The number of items to be displayed per page.
 * @returns {React.ReactNode} - The visual data component.
 */
export const ProfileDetailsDataItemVisualComponent: React.FC<profileDataVisualT> = ({
  title,
  data,
  className,
  badgeText,
  itemsPerPage = 3,
}: profileDataVisualT) => {
  const {currentPage, totalPages, currentData, handlePrev, handleNext, goToPage} = usePagination(data, itemsPerPage);

  return (
    <div className={`box ${className} h-min`}>
      <div className="box-header flex justify-between">
        <div className="box-title">{title}</div>
        {badgeText && <span className="badge bg-primary/10 text-primary">{badgeText}</span>}
      </div>
      <div className="box-body">
        <ul className="list-group">
          {currentData.map((item, index) => (
            <li className="list-group-item" key={index}>
              <Link to="#">
                <div className="flex flex-wrap items-center">
                  <span className="avatar avatar-md !mb-0 me-4">
                    <img src={item.src} className="img-fluid !rounded-md" alt="..." />
                  </span>
                  <div className="flex-grow">
                    <p className="mb-0 font-semibold">{item.name}</p>
                    <p className="profile-recent-posts text-truncate mb-0 text-[0.75rem] text-[#8c9097] dark:text-white/50">
                      {item.text}
                    </p>
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <div className="box-footer">
        <div className="items-center sm:flex">
          <div className="m-auto">
            <nav aria-label="Page navigation" className="pagination-style-4">
              <ul className="ti-pagination mb-0">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={handlePrev}>
                    Prev
                  </button>
                </li>
                {[...Array(totalPages).keys()].map(page => (
                  <li className={`page-item ${currentPage === page + 1 ? 'active' : ''}`} key={page}>
                    <button
                      className={`page-link ${currentPage === page + 1 ? 'active' : ''}`}
                      onClick={() => goToPage(page + 1)}
                    >
                      {page + 1}
                    </button>
                  </li>
                ))}
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={handleNext}>
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
};
